import { Button, Menu, Portal, Tabs, useBreakpointValue, useDisclosure } from "@chakra-ui/react-v3";
import { useEffect, useState } from "react";
import { useRefAndUpdateHeight } from "../../shared-logic/elementHeight";

const MAX_TABS_SHOWN = 8;

type TabOption = {
  index: number;
  display: string;
  id: string;
};

type TabTableData = {
  tableType: string;
  displayName: string;
  isEmptySection: boolean;
};

export function PatientTabsV2({
  hasAiSummary,
  isLoadingData = false,
  hasFeaturedData = false,
  filteredTableData = [],
}: {
  hasAiSummary: boolean;
  isLoadingData?: boolean;
  hasFeaturedData?: boolean;
  filteredTableData?: TabTableData[];
}) {
  const [tabsHeight, setTabsHeight] = useState<number>(0);
  const [index, setIndex] = useState(0);
  const [isManualScroll, setIsManualScroll] = useState(false);
  const { open, onOpen, onClose } = useDisclosure();

  const { elementRef, updateHeight } = useRefAndUpdateHeight(setTabsHeight);

  useEffect(() => {
    updateHeight();
    window.addEventListener("resize", updateHeight);
    return () => window.removeEventListener("resize", updateHeight);
  }, []);

  const tabOptions = generateTabOptions(hasAiSummary, hasFeaturedData, filteredTableData);
  const maxTabsShown =
    useBreakpointValue(
      {
        base: 3,
        lg: 4,
        xl: 6,
        "2xl": MAX_TABS_SHOWN,
      },
      { ssr: false }
    ) ?? 0;

  const totalOffset = tabsHeight;

  // Check if there are any overflow tabs that would go in "Other"
  const hasOverflowTabs = tabOptions.some(tab => tab.index >= maxTabsShown);

  useEffect(() => {
    function onScroll() {
      if (isManualScroll) {
        return;
      }
      const scrollIndex = setIndexIfInFrame(window.scrollY, tabOptions, totalOffset);

      if (scrollIndex === undefined) {
        return;
      }

      if (scrollIndex >= maxTabsShown) {
        setIndex(maxTabsShown);
        return;
      }

      setIndex(scrollIndex);
    }

    function onWheel() {
      setIsManualScroll(false);
    }

    window.removeEventListener("scroll", onScroll);
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("wheel", onWheel, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("wheel", onWheel);
    };
  }, [isManualScroll, tabOptions, maxTabsShown, totalOffset]);

  if (isLoadingData) {
    return null;
  }

  return (
    <Tabs.Root
      ref={elementRef}
      value={index.toString()}
      onValueChange={(details: { value: string }) => {
        const tabIndex = parseInt(details.value);
        if (tabIndex < maxTabsShown) {
          setIsManualScroll(true);
          scrollToIndex(tabIndex, tabOptions, totalOffset);
          setIndex(tabIndex);
        }
      }}
      mt={2}
      size={{ base: "sm", lg: "md" }}
      variant="plain"
      display={tabOptions.length < 1 ? "none" : ""}
    >
      <Tabs.List overflowX={"scroll"} justifyContent={maxTabsShown > 0 ? "left" : "right"}>
        {tabOptions
          .filter(tab => tab.index < maxTabsShown)
          .map((tab, i) => {
            return (
              <Tabs.Trigger
                value={tab.index.toString()}
                color="fg"
                whiteSpace={"nowrap"}
                key={tab.index}
                cursor={"pointer"}
                py={4}
                px={5}
                mr={4}
                ml={i === 0 ? 0 : 4}
                textStyle="body.md"
                _hover={{
                  bg: "brand.50",
                }}
                _active={{
                  bg: "brand.300",
                }}
                _selected={{
                  color: "brand.contrast",
                  bg: "brand.solid",
                  _hover: {
                    bg: "brand.400",
                  },
                  _active: {
                    bg: "brand.600",
                  },
                }}
              >
                {tab.display}
              </Tabs.Trigger>
            );
          })}
        {hasOverflowTabs && (
          <Menu.Root
            open={open}
            onOpenChange={(details: { open: boolean }) => (details.open ? onOpen() : onClose())}
            positioning={{ placement: "bottom", gutter: 8 }}
          >
            <Menu.Trigger
              fontWeight={"normal"}
              bg={index >= maxTabsShown ? "brand.solid" : "transparent"}
              color={index >= maxTabsShown ? "brand.contrast" : "fg"}
              _hover={{ bg: index >= maxTabsShown ? "brand.400" : "brand.50" }}
              _active={{ bg: index >= maxTabsShown ? "brand.600" : "brand.300" }}
              _focus={{ outline: "none", boxShadow: "none" }}
              _focusVisible={{ outline: "none", boxShadow: "none" }}
              cursor={"pointer"}
              onMouseEnter={onOpen}
              onMouseLeave={onClose}
              py={4}
              px={5}
              borderRadius="md"
              textStyle="body.md"
              height="40px"
              asChild
            >
              <Button size="md" variant="ghost">
                Other
              </Button>
            </Menu.Trigger>
            <Portal>
              <Menu.Positioner>
                <Menu.Content
                  onMouseEnter={onOpen}
                  onMouseLeave={onClose}
                  bg="bg"
                  borderColor="border"
                  borderWidth="1px"
                  borderRadius="md"
                  boxShadow="lg"
                  minW="200px"
                  p={1}
                >
                  {tabOptions
                    .filter(tab => tab.index >= maxTabsShown)
                    .map(tab => {
                      return (
                        <Menu.Item
                          value={tab.index.toString()}
                          color="fg"
                          key={tab.index}
                          cursor={"pointer"}
                          onClick={() => {
                            setIsManualScroll(true);
                            scrollToIndex(tab.index, tabOptions, totalOffset);
                            setIndex(tab.index);
                            onClose();
                          }}
                          textStyle="body.md"
                          _hover={{ bg: "interactive.hover" }}
                          borderRadius="sm"
                        >
                          {tab.display}
                        </Menu.Item>
                      );
                    })}
                </Menu.Content>
              </Menu.Positioner>
            </Portal>
          </Menu.Root>
        )}
      </Tabs.List>
    </Tabs.Root>
  );
}

/**
 * Generates tab options from filtered table data.
 * Uses TABLE_METADATA_FOR_INDEXING as the source of truth.
 * Only shows tabs for tables that have data after filtering.
 */
function generateTabOptions(
  hasAiSummary?: boolean,
  hasFeaturedData?: boolean,
  filteredTableData?: TabTableData[]
): TabOption[] {
  const tabOptions: TabOption[] = [];
  let currentIndex = 0;

  if (hasAiSummary) {
    tabOptions.push({ index: currentIndex++, id: "ai-summary", display: "AI Summary" });
  }

  if (hasFeaturedData) {
    tabOptions.push({ index: currentIndex++, id: "featured", display: "Featured" });
  }

  if (!filteredTableData) {
    return tabOptions;
  }

  // Only show tabs for tables that have filtered results
  filteredTableData.forEach(table => {
    if (!table.isEmptySection) {
      tabOptions.push({
        index: currentIndex++,
        id: table.tableType,
        display: table.displayName,
      });
    }
  });

  return tabOptions;
}

function setIndexIfInFrame(
  scrollOffset: number,
  tabOptions: { display: string; id: string }[],
  totalOffset: number
): number | undefined {
  const currentOffset = scrollOffset + totalOffset;
  const shadowHost = document.getElementById("shadow-root-host");

  for (let i = 0; i < tabOptions.length; i++) {
    const tab = tabOptions[i];
    if (!tab) continue;
    const element = shadowHost?.shadowRoot?.getElementById(tab.id);
    if (!element) {
      continue;
    }

    const elementPosition = element.getBoundingClientRect().top;
    const offsetPosition = elementPosition + window.pageYOffset - totalOffset;

    if (currentOffset >= offsetPosition && currentOffset < offsetPosition + element.clientHeight) {
      return i;
    }
  }
  return undefined;
}

function scrollToIndex(
  index: number,
  tabOptions: { display: string; id: string }[],
  totalOffset: number
) {
  const tab = tabOptions[index];
  if (!tab) return;
  const shadowHost = document.getElementById("shadow-root-host");
  const element = shadowHost?.shadowRoot?.getElementById(tab.id);
  if (!element) {
    return;
  }

  const elementPosition = element.getBoundingClientRect().top;
  const padding = 5;
  const offsetPosition = elementPosition + window.pageYOffset - totalOffset - padding;

  const stickyHeaderHeight = 65;
  window.scrollTo({
    top: offsetPosition - stickyHeaderHeight,
    behavior: "smooth",
  });
}
